import type { Board } from "./board";
import { Cadence } from "./cadence";
import { GoldDrop } from "./things/goldDrop";
import { DamageType } from "./damageType";
import { ALL_DIRECTIONS, Direction } from "./direction";
import { emitBloodEffect } from "./emitBloodEffect";
import {
	PRIORITY_DEFAULT,
	type ProcessableThing,
	type ProjectileHittable,
	type LookAtHorizontalDirection,
	type ControllableByAI,
	type ParticleEmitter,
	type Tintable,
	type TintConfig,
} from "./interfaces";
import { Hit } from "./effects/hit";
import type { SimpleBoard } from "./levelgenerator/simpleBoard";
import { MeleeHitMovementEffect } from "./effects/meleeHitMovementEffect";
import { MovementEffect } from "./effects/movementEffect";
import { PhysicalThing } from "./physicalThing";
import { BlackHoleProjectile } from "./projectiles/blackHoleProjectile";
import { randomElement } from "./random";
import { PhysicalThingWithHp, STD_BASE_HP } from "./physicalThingWithHp";
import { ParticleType } from "./particleType";
import type { Thing } from "./thing";
import { Chasm } from "./things/chasm";
import { LadderDown } from "./things/ladderDown";
import { LavaPool } from "./things/lavaPool";
import { Floor } from "./things/floors/floor";
import { MobCorpse } from "./mobs/mobCorpse";
import type { Sound } from "./things/sounds/sound";
import { WaterTile } from "./things/waterTile";
import type { XY } from "./xy";
import { ZOrder } from "./zOrder";

const DEFAULT_ATTACK_PERIOD = 2;
const MIN_DAMAGED_PARTICLE_INTENSITY = 0.3;
// Death sounds keep a near-normal distance gradient (so kills are localizable) but
// clamp to a floor instead of fading out, so a kill is audible from anywhere.
const DEATH_SOUND_VOLUME = 0.8;
const DEATH_SOUND_ATTENUATION_FACTOR = 0.7;
const DEATH_SOUND_MIN_VOLUME = 0.25;

export abstract class Mob
	extends PhysicalThingWithHp
	implements
		ProcessableThing,
		ProjectileHittable,
		LookAtHorizontalDirection,
		ControllableByAI,
		ParticleEmitter,
		Tintable
{
	lookDirection: Direction = Direction.E;
	frozenTurnsRemaining: number = 0;
	resistsPermanentFreeze: boolean = false;
	gold: number = 0;
	movementCadence: Cadence = new Cadence(1);
	attackCadence: Cadence = new Cadence(DEFAULT_ATTACK_PERIOD);

	constructor(xy: XY) {
		super(xy, false);
	}

	/** Move once every `turns` turns (1 = every turn). Resets the per-instance phase. */
	protected setMovementPeriod(turns: number): void {
		this.movementCadence = new Cadence(turns);
	}

	/** Attack once every `turns` turns (1 = every turn). Resets the per-instance phase. */
	protected setAttackPeriod(turns: number): void {
		this.attackCadence = new Cadence(turns);
	}

	staggerMovement(turns: number = 1): void {
		this.movementCadence.stagger(turns);
	}

	staggerAttack(turns: number = 1): void {
		this.attackCadence.stagger(turns);
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const mob = new (this as unknown as { new (xy: XY): Mob })(xy);
		mob.lookDirection = ALL_DIRECTIONS[Math.floor(Math.random() * ALL_DIRECTIONS.length)];
		board.putThing(mob);
		return [mob];
	}

	getZOrder(): number {
		return this.isFrozen() ? ZOrder.INACTIVE_MOB : ZOrder.MOB;
	}

	// A body never seals a tile against gas, not even a frozen one.
	override isGasPermeable(): boolean {
		return true;
	}

	// ProcessableThing
	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	processTurn(board: Board): void {
		if (this.isFrozen()) {
			if (this.frozenTurnsRemaining > 0) {
				this.frozenTurnsRemaining--;
				if (this.frozenTurnsRemaining === 0) {
					this.blocksMovement = false;
				}
			}
			return;
		}
		this.movementCadence.tick();
		this.attackCadence.tick();
		this.processMobAction(board);
	}

	abstract processMobAction(board: Board): void;

	// ProjectileHittable
	onProjectileHit(_projectile: unknown): void {}

	isHittable(): boolean {
		return true;
	}

	// LookAtHorizontalDirection
	getLookingAtHorizontalDirection(): Direction {
		return this.lookDirection;
	}

	// ControllableByAI
	getBoard(): Board {
		return this.board!;
	}

	setLookDirection(dir: Direction): void {
		this.lookDirection = dir;
	}

	moveTo(xy: XY): void {
		if (!this.movementCadence.isReady()) return;
		const oldXY = this.xy;
		this.xy = xy;
		this.board!.addEffect(new MovementEffect(this, oldXY, xy));
		this.movementCadence.consume();
	}

	attack(target: PhysicalThingWithHp): void {
		if (this.isFrozen()) return;
		if (this.xy.equals(target.xy)) return;
		this.board!.addEffect(new Hit(target.xy));
		target.applyDamage(DamageType.MELEE, this.getAttackDamage(), this);
		this.board!.addEffect(new MeleeHitMovementEffect(this, this.xy, target.xy));
		this.attackCadence.consume();
	}

	getXY(): XY {
		return this.xy;
	}

	canMoveTo(xy: XY): boolean {
		if (!this.movementCadence.isReady()) return false;
		return this.board!.isInBounds(xy) && this.board!.isPassable(xy) && !this.board!.hasMob(xy);
	}

	isFlying(): boolean {
		return false;
	}

	isMovementReady(): boolean {
		return this.movementCadence.isReady();
	}

	canAttack(target: PhysicalThingWithHp): boolean {
		if (!this.attackCadence.isReady()) return false;
		return target != null && target.xy.isInSquareRadius(this.xy, 1);
	}

	// Freeze
	freeze(turns: number, permanentFallbackTurns: number = 3): void {
		if (turns === -1 && this.resistsPermanentFreeze) {
			this.frozenTurnsRemaining = permanentFallbackTurns;
		} else {
			this.frozenTurnsRemaining = turns;
		}
		if (this.isFrozen()) {
			this.blocksMovement = true;
		}
		if (this.isPermanentlyFrozen() && this.board && this.countsAsKill()) {
			const mobKey = (this.constructor as any).key;
			this.board.stats.recordMobKill(mobKey);
			this.board.stats.recordKillByWeapon("CryoShot");
			if (this.gold > 0) {
				const goldXY = this.findAdjacentPassableCell() ?? this.xy;
				GoldDrop.spawnOrMerge(this.board, goldXY, this.gold);
				this.gold = 0;
			}
		}
	}

	unfreeze(): void {
		this.frozenTurnsRemaining = 0;
		this.blocksMovement = false;
	}

	isFrozen(): boolean {
		return this.frozenTurnsRemaining !== 0;
	}

	isPermanentlyFrozen(): boolean {
		return this.frozenTurnsRemaining === -1;
	}

	getBaseGoldValue(): number {
		return 0;
	}

	// Other
	getAttackDamage(): number {
		return STD_BASE_HP;
	}

	generateCorpse(): PhysicalThing | null {
		return null;
	}

	generateDeathDrop(): PhysicalThing | null {
		return null;
	}

	generateDeathSound(): Sound | null {
		return null;
	}

	// ParticleEmitter
	getParticleType(): ParticleType | null {
		if (this.isFrozen()) {
			return this.isPermanentlyFrozen() ? null : ParticleType.ICE_DRIP;
		}
		return this.hp < this.maxHp ? this.getDamageParticleType() : null;
	}

	getParticleIntensity(): number {
		if (this.isFrozen() && !this.isPermanentlyFrozen()) {
			return 0.5; // constant drip rate for temp frozen
		}
		if (this.maxHp <= 0) return 0;
		const damageFraction = 1 - this.hp / this.maxHp;
		return damageFraction > 0 ? Math.max(MIN_DAMAGED_PARTICLE_INTENSITY, damageFraction) : 0;
	}

	// Tintable
	getTint(): TintConfig | null {
		if (this.isPermanentlyFrozen()) return { r: 100, g: 150, b: 255, minAlpha: 0.65, maxAlpha: 0.65 };
		if (this.isFrozen()) return { r: 140, g: 190, b: 255, minAlpha: 0.25, maxAlpha: 0.45 };
		return null;
	}

	getAnimationSpeedMultiplier(): number {
		if (this.isPermanentlyFrozen()) return 0;
		if (this.isFrozen()) return 0.1;
		return 1.0;
	}

	getDamageParticleType(): ParticleType | null {
		switch (this.getBloodParticleType()) {
			case ParticleType.BLOOD:
				return ParticleType.BLOOD_DRIP;
			case ParticleType.BLUE_BLOOD:
				return ParticleType.BLUE_BLOOD_DRIP;
			case ParticleType.ROBOT_DEBRIS:
				return ParticleType.DAMAGE_SMOKE;
			default:
				return null;
		}
	}

	getBloodParticleType(): ParticleType | null {
		return null;
	}

	applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const dmg = super.applyDamage(type, amount, source);
		const bloodType = this.getBloodParticleType();
		if (dmg > 0 && bloodType !== null && this.board && !this.isPermanentlyFrozen()) {
			emitBloodEffect(this.board, this.xy, bloodType, type, source);
		}
		return dmg;
	}

	countsAsKill(): boolean {
		return true;
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		// causes a dead mob to stop blocking movement immediately
		this.blocksMovement = false;
		if (this.board && this.countsAsKill() && !this.isPermanentlyFrozen()) {
			const mobKey = (this.constructor as any).key;
			this.board.stats.recordMobKill(mobKey);
			if (source) {
				const sourceKey = (source.constructor as any).key;
				this.board.stats.recordKillByWeapon(sourceKey);
			}
		}
		if (this.gold > 0 && this.board && !this.isOverOpenChasmOrLava()) {
			const goldXY = type === DamageType.BUBBLE ? (this.findAdjacentPassableCell() ?? this.xy) : this.xy;
			GoldDrop.spawnOrMerge(this.board, goldXY, this.gold);
		}
		const drop = this.generateDeathDrop();
		if (drop !== null && this.board) {
			// Never on the death tile — corpse and gold land there and would bury the drop.
			drop.xy = this.findDropCell() ?? this.xy;
			this.board.addThing(drop);
		}
		if (type === DamageType.BUBBLE) return;
		const deathSound = this.generateDeathSound();
		if (deathSound !== null && !this.isPermanentlyFrozen()) {
			deathSound.xy = this.xy;
			deathSound.volume = DEATH_SOUND_VOLUME;
			deathSound.attenuationFactor = DEATH_SOUND_ATTENUATION_FACTOR;
			deathSound.minVolume = DEATH_SOUND_MIN_VOLUME;
			this.board!.addNonPhysicalThing(deathSound);
		}
		// Skip corpse if mob is on an environmental tile that would consume it
		if (this.isOnEnvironmentalTile()) return;
		const corpse = this.generateCorpse();
		if (corpse !== null) {
			for (const t of this.board!.getThingsAt(this.xy)) {
				if (t instanceof MobCorpse) this.board!.removeThing(t);
			}
			corpse.xy = this.xy;
			this.board!.addThing(corpse);
		}
	}

	private isOnEnvironmentalTile(): boolean {
		const things = this.board!.getThingsAt(this.xy);
		for (const t of things) {
			if (t instanceof Chasm || t instanceof LavaPool) {
				return !this.board!.isPassable(this.xy);
			}
			if (t instanceof WaterTile) {
				// A bridge (Floor) over the water lets the corpse rest on it.
				return !things.some((x) => x instanceof Floor);
			}
			if (t instanceof LadderDown) {
				return true;
			}
		}
		return false;
	}

	protected isOverOpenChasmOrLava(): boolean {
		const things = this.board!.getThingsAt(this.xy);
		return things.some((t) => (t instanceof Chasm || t instanceof LavaPool) && !this.board!.isPassable(this.xy));
	}

	private findAdjacentPassableCell(): XY | null {
		const candidates = this.xy.getNeighbors().filter((n) => this.board!.isPassable(n));
		return randomElement(candidates) ?? null;
	}

	private findDropCell(): XY | null {
		const holes = this.board!
			.getAllThings()
			.filter((t): t is BlackHoleProjectile => t instanceof BlackHoleProjectile && t.active);
		const candidates = this.xy
			.getNeighbors()
			.filter((n) => this.board!.isPassable(n) && !holes.some((h) => h.covers(n)));
		return randomElement(candidates) ?? null;
	}

	protected restoreMobFields(data: any): void {
		this.restoreBaseFields(data);
		this.lookDirection = data.lookDirection;
		this.frozenTurnsRemaining = data.frozenTurnsRemaining ?? 0;
		this.gold = data.gold ?? 0;
		if (data.movementCooldown !== undefined) this.movementCadence.restore(data.movementCooldown);
		if (data.attackCooldown !== undefined) this.attackCadence.restore(data.attackCooldown);
		if (this.isFrozen()) {
			this.blocksMovement = true;
		}
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = {
			...super.serialize(),
			lookDirection: this.lookDirection,
		};
		if (this.frozenTurnsRemaining !== 0) {
			data.frozenTurnsRemaining = this.frozenTurnsRemaining;
		}
		if (this.gold > 0) {
			data.gold = this.gold;
		}
		if (this.movementCadence.period > 1 || this.movementCadence.cooldown > 0) {
			data.movementCooldown = this.movementCadence.cooldown;
		}
		if (this.attackCadence.period > 1 || this.attackCadence.cooldown > 0) {
			data.attackCooldown = this.attackCadence.cooldown;
		}
		return data;
	}
}
